import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faCrown,
    faSpinner,
    faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { faGoogle } from '@fortawesome/free-brands-svg-icons';
import { useAuth } from '../hooks/useAuth';
import { shareEventLink } from '../utils/shareEvent';
import { useEvents } from '../hooks/useEvents';
import { useEventsPagination } from '../hooks/useEventsPagination';
import { useDarkMode } from '../hooks/useDarkMode';
import { useEventFilters } from '../hooks/useEventFilters';
import LoadingSpinner from '../components/common/LoadingSpinner';
import AppHeader from '../components/common/AppHeader';
import EventFilters from '../components/my-events/EventFilters';
import EventList from '../components/my-events/EventList';
import FeaturedEventCard from '../components/my-events/FeaturedEventCard';
import MobileActionBar, { MobileAction } from '../components/common/MobileActionBar';
import { useTranslation } from 'react-i18next';

const MyEventsPage: React.FC = () => {
    const { user, loading: authLoading, signInWithGoogle } = useAuth();
    const { darkMode, toggleDarkMode } = useDarkMode();
    const { deleteEvent, removeEventHistory } = useEvents();
    const { t } = useTranslation();

    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'closed'>('all');
    const [filterRole, setFilterRole] = useState<'all' | 'owner' | 'joined'>('all');
    const [copiedEventId, setCopiedEventId] = useState<string | null>(null);

    // Use filter-aware pagination. Signed out, it lists the events opened on
    // this device instead.
    const {
        events,
        loading,
        hasMore,
        loadMore,
        totalCount,
        filteredCount
    } = useEventsPagination(user?.uid, filterStatus, !authLoading);

    // Creating an event takes an account, so a guest is offered that first;
    // their events here are carried into it (see mergeLocalHistory).
    const handleSignIn = () => {
        signInWithGoogle().catch(() => {
            // Already logged by useAuth — usually just the popup being closed
        });
    };

    // Apply remaining filters (search + sort + role) to paginated events
    const { filteredAndSortedEvents, eventStatusMap } = useEventFilters({
        events,
        searchTerm,
        filterStatus: 'all',
        sortBy: 'date',
        filterRole,
        currentUserId: user?.uid
    });

    // The list keeps showing everything, active and closed alike — this only lifts
    // one event out of the grid so the eye has somewhere to land. It is the newest
    // event still open: the newest one overall is often already settled and closed,
    // which makes for a poor thing to be pointed at.
    // A search is its own focus, so the highlight steps aside for one.
    const featuredEvent = useMemo(() => {
        if (searchTerm) return null;
        return filteredAndSortedEvents.find(event => eventStatusMap[event.id] === true) || null;
    }, [filteredAndSortedEvents, eventStatusMap, searchTerm]);

    const listedEvents = useMemo(() => (
        featuredEvent
            ? filteredAndSortedEvents.filter(event => event.id !== featuredEvent.id)
            : filteredAndSortedEvents
    ), [filteredAndSortedEvents, featuredEvent]);

    // Infinite scroll with IntersectionObserver
    const sentinelRef = useRef<HTMLDivElement>(null);

    // The bottom bar's two actions. Search is here rather than only at the top of
    // the page because the filter row scrolls away after a screenful of events,
    // and hunting for it is the reason people scroll back up at all.
    const searchInputRef = useRef<HTMLInputElement>(null);
    const focusSearch = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        searchInputRef.current?.focus({ preventScroll: true });
    };

    const mobileActions: MobileAction[] = [
        user ? {
            key: 'create',
            label: t('myEvents.newEvent'),
            icon: faPlus,
            to: '/create-event',
            primary: true,
        } : {
            key: 'sign-in',
            label: t('auth.signIn'),
            icon: faGoogle,
            onClick: handleSignIn,
            primary: true,
        },
        // Nothing to focus until there are events to filter
        ...(events.length > 0 ? [{
            key: 'search',
            label: t('myEvents.filters.searchAction'),
            icon: faSearch,
            onClick: focusSearch,
        }] : []),
    ];

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel || !hasMore || loading) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    loadMore();
                }
            },
            { rootMargin: '200px' }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMore, loading, loadMore]);

    // Event handlers
    const handleShareEvent = async (eventId: string) => {
        try {
            await shareEventLink(eventId);
            setCopiedEventId(eventId);
            setTimeout(() => setCopiedEventId(null), 2000);
        } catch {
            // User cancelled share
        }
    };

    const handleDeleteEvent = async (eventId: string) => {
        try {
            await deleteEvent(eventId);
        } catch (error) {
            console.error('Error deleting event:', error);
            throw error;
        }
    };

    const handleRemoveHistory = async (eventId: string) => {
        try {
            await removeEventHistory(user?.uid, eventId);
        } catch (error) {
            console.error('Error removing history:', error);
            throw error;
        }
    };

    const clearFilters = () => {
        setSearchTerm('');
        setFilterStatus('all');
        setFilterRole('all');
    };

    // Loading state
    if (authLoading || (loading && events.length === 0)) {
        return <LoadingSpinner />;
    }

    return (
        <div className="glass-page-bg">
            <AppHeader
                title={t('myEvents.title')}
                subtitle={user
                    ? t('myEvents.subtitle', { count: totalCount })
                    : t('myEvents.subtitleGuest', { count: totalCount })}
                icon={faCrown}
                darkMode={darkMode}
                onToggleDarkMode={toggleDarkMode}
                rightActions={user ? (
                    <Link
                        to="/create-event"
                        className="glass-btn-primary hidden lg:flex items-center !px-4 !py-2 text-sm"
                    >
                        <FontAwesomeIcon icon={faPlus} className="md:mr-2 mr-0" />
                        <span className="hidden sm:inline">{t('myEvents.newEvent')}</span>
                    </Link>
                ) : (
                    <button
                        type="button"
                        onClick={handleSignIn}
                        className="glass-btn-primary hidden lg:flex items-center !px-4 !py-2 text-sm"
                    >
                        <FontAwesomeIcon icon={faGoogle} className="mr-2" />
                        {t('auth.signIn')}
                    </button>
                )}
            />

            {/* pb leaves room for the fixed bottom action bar */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-28 lg:pb-8">
                {/* Filters and Search */}
                {events.length > 0 && (
                    <EventFilters
                        searchTerm={searchTerm}
                        filterStatus={filterStatus}
                        filterRole={filterRole}
                        onSearchChange={setSearchTerm}
                        onFilterChange={setFilterStatus}
                        onFilterRoleChange={setFilterRole}
                        // A guest owns nothing, so Mine/Viewed would split nothing
                        showRoleFilter={Boolean(user)}
                        searchInputRef={searchInputRef}
                    />
                )}

                {/* Newest still-open event, pulled out of the grid */}
                {featuredEvent && (
                    <FeaturedEventCard
                        event={featuredEvent}
                        copiedEventId={copiedEventId}
                        onShareLink={handleShareEvent}
                    />
                )}

                {/* A labelled rule between the highlight and the grid, so the two
                    read as separate sections rather than one big card on top of
                    some smaller ones. */}
                {featuredEvent && listedEvents.length > 0 && (
                    <div className="flex items-center gap-3 mb-4">
                        <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500 whitespace-nowrap">
                            {t('myEvents.featured.othersLabel')}
                        </span>
                        <span className="text-xs text-gray-400 dark:text-gray-600">{listedEvents.length}</span>
                        <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                    </div>
                )}

                {/* Events List — skipped entirely when the highlight above already
                    accounts for every matching event, so nothing renders an
                    "no events found" state underneath it. */}
                {!(featuredEvent && listedEvents.length === 0) && (
                    <EventList
                        events={events}
                        filteredEvents={listedEvents}
                        copiedEventId={copiedEventId}
                        onShareLink={handleShareEvent}
                        onClearFilters={clearFilters}
                        onDeleteEvent={handleDeleteEvent}
                        onRemoveHistory={handleRemoveHistory}
                        currentUserId={user?.uid}
                        totalEventCount={totalCount}
                        eventStatusMap={eventStatusMap}
                    />
                )}

                {/* Infinite Scroll Sentinel */}
                <div ref={sentinelRef} className="flex justify-center py-6">
                    {loading && events.length > 0 && (
                        <div className="flex items-center text-gray-500 dark:text-gray-400 text-sm">
                            <FontAwesomeIcon icon={faSpinner} className="mr-2 animate-spin" />
                            {t('myEvents.loading')}
                        </div>
                    )}
                </div>

                {/* Pagination Info */}
                {totalCount > 0 && (
                    <div className="text-center text-sm text-gray-500 dark:text-gray-400">
                        {t('myEvents.pagination', { showing: events.length, count: filteredCount })}
                        {filteredCount !== totalCount && (
                            <> {t('myEvents.paginationTotal', { total: totalCount })}</>
                        )}
                    </div>
                )}
            </div>

            <MobileActionBar actions={mobileActions} />
        </div>
    );
};

export default MyEventsPage;